import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// --- Auth interceptor (registrado 1x na importação do módulo) ---------------
// Anexa `Authorization: Bearer <token>` APENAS a chamadas para nossa própria
// API (REACT_APP_BACKEND_URL). Nunca envia token para domínios externos como
// Z-API, Google, Meta ou CDNs. Nunca sobrescreve header explícito.
const _OUR_API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function _urlBelongsToOurApi(url) {
  if (!url || typeof url !== "string") return false;
  // Paths relativos ("/api/x") sempre são nossos
  if (url.startsWith("/")) return true;
  // URLs absolutas: precisa começar EXATAMENTE com nossa base
  return !!_OUR_API_BASE && url.startsWith(_OUR_API_BASE);
}

// idempotência do registro (HMR / re-import): evita registrar 2x
if (!axios.__ourAuthInterceptorInstalled) {
  axios.interceptors.request.use((config) => {
    try {
      if (!_urlBelongsToOurApi(config.url || "")) return config;
      if (config.headers && config.headers.Authorization) return config;
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (_e) {
      /* localStorage inaccessível → ignore */
    }
    return config;
  });
  axios.__ourAuthInterceptorInstalled = true;
}

// Normalize FastAPI/Pydantic validation errors (422 -> detail is array of objects)
// into a flat string so call sites can safely render e.response.data.detail
// without crashing React.
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (detail != null && typeof detail !== "string") {
      let msg;
      if (Array.isArray(detail)) {
        msg = detail
          .map((d) => {
            if (typeof d === "string") return d;
            if (d && typeof d === "object") {
              const loc = Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
              return loc ? `${loc}: ${d.msg || JSON.stringify(d)}` : (d.msg || JSON.stringify(d));
            }
            return String(d);
          })
          .join("; ");
      } else if (typeof detail === "object") {
        msg = detail.msg || JSON.stringify(detail);
      } else {
        msg = String(detail);
      }
      err.response.data.detail = msg;
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/auth/me`, { headers, withCredentials: true });
      setUser(res.data);
    } catch (e) {
      setUser(null);
      localStorage.removeItem("access_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("access_token", res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("access_token", res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem("access_token");
    setUser(null);
  };

  const apiCall = useCallback(async (method, url, data = null, opts = {}) => {
    const token = localStorage.getItem("access_token");
    const unitId = localStorage.getItem("selected_unit_id");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    if (unitId && unitId !== "all") headers["X-Unit-Id"] = unitId;
    if (opts && opts.headers) Object.assign(headers, opts.headers);
    const config = { headers, withCredentials: true, ...(opts || {}) };
    // headers foi mesclado acima; garante que o config final tenha a versão consolidada
    config.headers = headers;
    if (method === "get") return axios.get(`${API}${url}`, config);
    if (method === "delete") return axios.delete(`${API}${url}`, config);
    if (method === "post") return axios.post(`${API}${url}`, data, config);
    if (method === "put") return axios.put(`${API}${url}`, data, config);
    if (method === "patch") return axios.patch(`${API}${url}`, data, config);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, apiCall, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
